import { Badge } from '@radix-ui/themes'
import { ComponentProps } from 'react'
import { PamPlayerStatus, PamPlayerVerificationStatus } from '@Generated/back-office-client/src'
import { getStatusDisplay } from './pam-verification-status-display'

type BadgeColor = ComponentProps<typeof Badge>['color']

export type AccountStatusDisplay = { label: string; color: BadgeColor }

const ACCOUNT_STATUS_DISPLAY: Record<string, AccountStatusDisplay> = {
  [PamPlayerStatus.active]: { label: 'Active', color: 'green' },
  [PamPlayerStatus.probationary]: { label: 'Probationary', color: 'amber' },
  [PamPlayerStatus.suspended]: { label: 'Suspended', color: 'red' },
  [PamPlayerStatus.deactivated]: { label: 'Deactivated', color: 'orange' },
  [PamPlayerStatus.self_excluded]: { label: 'Self-excluded', color: 'gray' },
}

export const getAccountStatusDisplay = (status: string): AccountStatusDisplay => {
  return ACCOUNT_STATUS_DISPLAY[status] ?? { label: status, color: 'gray' }
}

export type BannerDisplay = { color: BadgeColor; attention: boolean }

// Account statuses a player can sign in with. Every other status blocks sign-in.
const OPEN_ACCOUNT_STATUSES: ReadonlySet<string> = new Set([
  PamPlayerStatus.active,
  PamPlayerStatus.probationary,
])

// Verification states that warrant a back-office "needs attention" treatment.
// submitted/not_started/approved/manually_approved are calm.
const ATTENTION_VERIFICATION_STATUSES: ReadonlySet<string> = new Set([
  PamPlayerVerificationStatus.rejected,
  PamPlayerVerificationStatus.locked,
  PamPlayerVerificationStatus.denied,
  PamPlayerVerificationStatus.duplicate_review,
])

/**
 * Drives the player-details account banner: the status's badge colour, with attention for any
 * status that blocks sign-in.
 */
export const getAccountBannerDisplay = (accountStatus: string): BannerDisplay => {
  return {
    color: getAccountStatusDisplay(accountStatus).color,
    attention: !OPEN_ACCOUNT_STATUSES.has(accountStatus),
  }
}

/**
 * Drives the player-details KYC banner from the verification status alone. Account status is
 * independent and has its own banner.
 */
export const getVerificationBannerDisplay = (verificationStatus: string): BannerDisplay => {
  return {
    color: getStatusDisplay(verificationStatus).color,
    attention: ATTENTION_VERIFICATION_STATUSES.has(verificationStatus),
  }
}
